import * as Actions from '../../../../redux/actions';
import {
    DirectionsRenderer,
    GoogleMap,
    withGoogleMap,
} from 'react-google-maps';
import { compose, lifecycle, withProps } from 'recompose';
import React from 'react';
import mapStyles from '../../TripPage/mapStyles';
import DestinationMarkers from '../destinationMarkers';
import Polyline from 'react-google-maps/lib/components/Polyline';
import { useDispatch, useSelector } from 'react-redux';
import { getDestinationNearbyPlaces } from './ActiveTrip.slice';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';
import { generateMapAPIKey } from '../../../../utility/utility';
import { renderIconDestination } from './DestinationIconsMap';
import PlacesMarkers from '../PlacesMarkers';

const isEqual = (arr1 = [], arr2 = []) =>
    arr1.length === arr2.length &&
    arr1.every((a) => arr2.some((b) => JSON.stringify(a) === JSON.stringify(b)));

const fetchRoute = (origin, destination, transportMode, callback) => {
    const DirectionsService = new window.google.maps.DirectionsService();
    const isTrain = transportMode === 'train';

    DirectionsService.route(
        {
            origin,
            destination,
            provideRouteAlternatives: true,
            optimizeWaypoints: true,
            travelMode: isTrain
                ? window.google.maps.TravelMode.TRANSIT
                : window.google.maps.TravelMode.DRIVING,
            transitOptions: {
                modes: isTrain ? [window.google.maps.TransitMode.TRAIN] : undefined,
            },
        },
        (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
                callback(result);
            } else {
                console.warn('Route fetch failed', status);
            }
        }
    );
};

export const DestinationMap = compose(
    withProps({
        googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${generateMapAPIKey()}&v=3.exp&libraries=geometry,drawing,places`,
        loadingElement: <div style={{ height: `100%` }} />,
        containerElement: <div style={{ height: `100%` }} />,
        mapElement: <div style={{ height: `100%` }} />,
    }),
    withGoogleMap,
    lifecycle({
        initializeMap(destinations) {
            const mapPoints = [];
            this.setState({ directions: {}, allDirections: [], display: false });

            destinations.forEach((dest) => {
                const { transportMode, latitude, longitude, mapGeo, home } = dest;
                if (home) return;

                const destPath = { lat: +latitude, lng: +longitude };
                mapPoints.push(destPath);

                const isFlight = transportMode === 'plane';

                if (isFlight && mapGeo) {
                    const airportOrigin = {
                        lat: +mapGeo.origin.airportLatitude || +mapGeo.origin.latitude,
                        lng: +mapGeo.origin.airportLongitude || +mapGeo.origin.longitude,
                    };
                    const airportDest = {
                        lat: +mapGeo.destination.airportLatitude || +mapGeo.destination.latitude,
                        lng: +mapGeo.destination.airportLongitude || +mapGeo.destination.longitude,
                    };

                    const userStartLoc = {
                        lat: +mapGeo.origin.latitude,
                        lng: +mapGeo.origin.longitude,
                    };

                    const finalDestLoc = {
                        lat: +latitude,
                        lng: +longitude,
                    };

                    fetchRoute(userStartLoc, airportOrigin, 'car', (result) => {
                        this.setState((prev) => ({
                            allDirections: [
                                ...prev.allDirections,
                                { directions: result, transportMode: 'car' },
                            ],
                        }));
                    });

                    fetchRoute(airportDest, finalDestLoc, 'car', (result) => {
                        this.setState((prev) => ({
                            allDirections: [
                                ...prev.allDirections,
                                { directions: result, transportMode: 'car' },
                            ],
                        }));
                    });
                } else if (mapGeo) {
                    const path1 = {
                        lat: +mapGeo.origin.latitude,
                        lng: +mapGeo.origin.longitude,
                    };
                    const path2 = {
                        lat: +mapGeo.destination.latitude,
                        lng: +mapGeo.destination.longitude,
                    };

                    fetchRoute(path1, path2, transportMode, (result) => {
                        this.setState((prev) => ({
                            allDirections: [
                                ...prev.allDirections,
                                { directions: result, transportMode },
                            ],
                        }));
                    });
                }
            });

            if (mapPoints.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                mapPoints.forEach((pt) => bounds.extend(pt));
                this.props.refMap.current.fitBounds(bounds);
                this.props.setMapCenter(bounds.getCenter());
                this.props.setZoom(8);
            }
        },

        componentDidMount() {
            this.initializeMap(this.props.destinations);
        },

        componentDidUpdate(prevProps) {
            if (
                prevProps.tripId !== this.props.tripId ||
                !isEqual(prevProps.destinations, this.props.destinations)
            ) {
                this.initializeMap(this.props.destinations);
            }
        },
    })
)((props) => {
    const dispatch = useDispatch();
    const destinationNearByPlaces = useSelector((state) => state.ActiveTrip.destinationNearByPlaces);
    const destinationNearByDestinations = useSelector(state => state.ActiveTrip.destinationNearByDestinations);

    const [trackInfoArray, setTrackInfoArray] = React.useState([]);
    const [trackDestArray, setTrackDestArray] = React.useState([]);

    const pushPlacesWindow = (id) => setTrackInfoArray([id]);
    const pushDestWindow = (id) => setTrackDestArray([id]);
    const clearDestWindow = () => setTrackDestArray([]);
    const clearPlacesWindow = () => setTrackInfoArray([]);

    const checkDestOpen = (id) => trackDestArray.includes(id);
    const checkWindowOpen = (id) => trackInfoArray.includes(id);

    React.useEffect(() => {
        if (props.overviewPath) {
            dispatch(
                getDestinationNearbyPlaces({
                    coords: props.overviewPath,
                    selectedTypes: props.selectedTypes || [],
                })
            );
        }
    }, [props.overviewPath]);

    return (
        <GoogleMap
            defaultZoom={props.zoom}
            zoom={props.zoom}
            defaultCenter={props.mapCenter}
            center={props.mapCenter}
            options={{
                controlSize: 20,
                mapTypeControl: false,
                fullscreenControl: false,
                styles: mapStyles,
                gestureHandling: props?.showRefresh ? 'none' : 'auto',
                minZoom: 3,
            }}
            ref={props.refMap}
            onClick={() => {
                clearPlacesWindow();
                clearDestWindow();
            }}
        >
            {props.destinations?.map((dest, index) => {
                const isFlight = dest.transportMode === 'plane';
                const geo = dest.mapGeo;
                if (!isFlight || !geo) return null;

                const origin = {
                    lat: +geo.origin.airportLatitude || +geo.origin.latitude,
                    lng: +geo.origin.airportLongitude || +geo.origin.longitude,
                };
                const destination = {
                    lat: +geo.destination.airportLatitude || +geo.destination.latitude,
                    lng: +geo.destination.airportLongitude || +geo.destination.longitude,
                };

                return (
                    <Polyline
                        key={`flight-${index}`}
                        path={[origin, destination]}
                        options={{
                            suppressMarkers: true,
                            strokeColor: '#19BC9B',
                            strokeOpacity: 0.5,
                            strokeWeight: 4,
                            geodesic: true,
                            icons: [
                                {
                                    icon: renderIconDestination('plane'),
                                    offset: '50%',
                                },
                            ],
                        }}
                    />
                );
            })}



            <MarkerClusterer
                averageCenter
                enableRetinaIcons
                gridSize={100}
                defaultMinimumClusterSize={2}
                calculator={(markers, numStyles) => {
                    if (markers.length >= 0 && markers.length < 10) return { text: markers.length, index: 1 };
                    return { text: markers.length, index: 0 };
                }
                }
                styles={[{
                    height: 36,
                    url: "https://storage.googleapis.com/muvimages/muv_assets/icons/Destination%20cluster2.png",
                    width: 35,
                    textColor: "white",
                }
                ]}
            >

                {destinationNearByDestinations && props.showNearBy && destinationNearByDestinations.length > 0 && destinationNearByDestinations.map((destination, index_destination) => {
                    const lengthDestinations = destinationNearByDestinations.length;
                    return (
                        <DestinationMarkers key={index_destination} lengthDestinations={lengthDestinations} destination={destination} color={{ fill: 'rgba(216, 82, 29, 1)', stroke: 'rgba(216, 82, 29, 0.4)' }}
                            index_destination={index_destination} mapProps={props} isRegularTrip={false} textColor={"rgba(25, 188, 155, 1)"}
                            pushDestWindow={pushDestWindow}
                            clearDestWindow={clearDestWindow}
                            checkDestOpen={checkDestOpen}
                            dblClickDestination={props?.dblClickDestination} />
                    )
                })}
            </MarkerClusterer>


            <MarkerClusterer
                averageCenter
                enableRetinaIcons
                gridSize={60}
                defaultMinimumClusterSize={5}
                // calculator={(markers,numStyles)=>{
                //     return {text: markers.length, index: 0};    }          
                // }
                styles={[{
                    height: 36,
                    url: "https://storage.googleapis.com/muvimages/muv_assets/icons/Activity%20cluster2.png",
                    width: 35,
                    textColor: "white",
                },]}
            >

                {/* show nearby places if its not a flight */}
                {destinationNearByPlaces && props.selectedTypes && props.selectedTypes.length > 0 &&
                    destinationNearByPlaces.length > 0 && destinationNearByPlaces.map((establishment, index_establishment) => {


                        const typeID = establishment?.types && establishment?.types[0]?.typeID;
                        const type = props.types ? props.types.find(type => type.typeID === typeID) : null;
                        const icon = type && type.icon ? type.icon : null;
                        const zoom = props && props.refMap ? props.refMap?.current?.getZoom() : 55;

                        return (
                            <React.Fragment key={index_establishment}>

                                <PlacesMarkers zoom={zoom} icon={icon} establishment={establishment} index_establishment={index_establishment} placesNearByCenter={destinationNearByPlaces}
                                    pushPlacesWindow={pushPlacesWindow}
                                    clearPlacesWindow={clearPlacesWindow}
                                    checkWindowOpen={checkWindowOpen}
                                />
                            </React.Fragment>
                        )
                    })}
            </MarkerClusterer>


            {props.destinations?.map((dest, index) => (
                <DestinationMarkers
                    key={index}
                    lengthDestinations={props.destinations.length}
                    destination={dest}
                    color={{
                        fill: 'rgba(25, 188, 155, 1)',
                        stroke: 'rgba(25, 188, 155, 0.4)',
                    }}
                    index_destination={index}
                    mapProps={props}
                    isRegularTrip={false}
                    pushDestWindow={pushDestWindow}
                    clearDestWindow={clearDestWindow}
                    checkDestOpen={checkDestOpen}
                    isItinerary={true}
                    dblClickDestination={props?.dblClickDestination}
                />
            ))}

            {props.allDirections?.map((route, index) => (
                <DirectionsRenderer
                    key={`dir-${index}`}
                    directions={route.directions}
                    options={{
                        preserveViewport: true,
                        suppressMarkers: true,
                        polylineOptions: {
                            strokeColor: '#19BC9B',
                            strokeOpacity: 1,
                            strokeWeight: 4,
                            icons: [
                                {
                                    icon: renderIconDestination(route.transportMode),
                                    offset: '50%',
                                    fixedRotation: true,
                                },
                            ],
                        },
                    }}
                />
            ))}
        </GoogleMap>
    );
});
